/**
 * Types untuk menu "Home Page" (pengelola konten halaman Home website).
 *
 * Setiap section di Home didefinisikan lewat `HomeSectionConfig`
 * (lihat src/helpers/homePageSections.ts). Halaman edit membaca config
 * tersebut dan merender form-nya secara generik, jadi menambah section
 * baru cukup dengan menambah satu blok config.
 */

export type HomeSectionKey =
  | "header"
  | "hero"
  | "categories"
  | "about"
  | "materials"
  | "industries"
  | "recent_project"
  | "clients"
  | "client_story"
  | "achievements"
  | "about_hero"
  | "vision"
  | "mission"
  | "core_values"
  | "footer";

/** Halaman website tempat section ini tampil (untuk pengelompokan di daftar). */
export type HomeSectionPage = "Home" | "About Us";

export type HomeFieldType = "text" | "textarea" | "email" | "image";

export interface HomeFieldConfig {
  key: string;
  label: string;
  type: HomeFieldType;
  placeholder?: string;
  required?: boolean;
  /** Hanya untuk type "textarea". */
  rows?: number;
  /** Teks bantuan kecil di bawah field. */
  hint?: string;
  /**
   * Field ini konten kalimat yang tampil ke pengunjung website (judul,
   * deskripsi, dst) -> diedit sebagai pasangan Indonesia + English
   * berdampingan. Nilai Inggris disimpan di key `${key}_en`.
   * Field non-translatable (link, nomor telepon, email, dst) tetap satu
   * input seperti biasa -- sama untuk kedua bahasa.
   * Tidak berlaku untuk field bertipe "image" (gambar selalu 1, dipakai
   * bersama kedua bahasa).
   */
  translatable?: boolean;
}

/** Konfigurasi daftar item berulang (mis. card, logo client, testimoni). */
export interface HomeItemsConfig {
  /** Judul blok, mis. "Cards". */
  label: string;
  /** Nama satu item, mis. "Card" -> tombol "Add Card". */
  itemLabel: string;
  /** Key field yang dipakai sebagai judul ringkas tiap item. */
  titleKey: string;
  fields: HomeFieldConfig[];
  min?: number;
  max?: number;
}

export interface HomeSectionNote {
  text: string;
  linkLabel: string;
  path: string;
}

export interface HomeSectionConfig {
  key: HomeSectionKey;
  /** Kosong = "Home". */
  page?: HomeSectionPage;
  title: string;
  description: string;
  fields: HomeFieldConfig[];
  items?: HomeItemsConfig;
  /** Info tambahan di atas form (mis. arahan ke menu lain). */
  note?: HomeSectionNote;
}

/** Nilai satu field: teks, File baru (image), URL lama (image), atau null (image kosong). */
export type HomeFieldValue = string | File | null;

export type HomeSectionValues = Record<string, HomeFieldValue>;

/**
 * Konvensi key untuk pasangan field translatable: versi Indonesia disimpan
 * di `field.key` (seperti sebelum ada i18n, tidak berubah), versi Inggris
 * di `${field.key}_en`. Dipakai bersama oleh helpers/homePageForm.ts dan
 * commponents/homePage/SectionFields.tsx supaya konsisten.
 */
export const EN_KEY_SUFFIX = "_en";
export const toEnKey = (key: string): string => `${key}${EN_KEY_SUFFIX}`;

export interface HomeSectionItem {
  /** Id lokal untuk React key. Bukan id database. */
  uid: string;
  /** Id database (kalau item sudah tersimpan). */
  id?: number;
  values: HomeSectionValues;
}

/** Bentuk data satu section yang dikembalikan backend. */
export interface HomeSectionData {
  updated_at?: string | null;
  items?: Array<Record<string, unknown> & { id?: number }>;
  [field: string]: unknown;
}